import React, { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';
import { getOperationalMetricMeta } from '../../../constants/operationalMetricCatalog';
import { formatMoneyMetricValue } from './metricFormat';
import { ChartWrap } from './styles';

/**
 * @param {{ metricKey?: string, entries?: Array<{ id: number, label: string, value: number }>, loading?: boolean }} props
 */
export default function RankingChart({ metricKey, entries = [], loading = false }) {
    const theme = useTheme();
    const fill = theme?.colors?.primary?.main || '#3B5998';
    const { rankingFormat } = getOperationalMetricMeta(metricKey);
    const isMoney = rankingFormat === 'money';
    const seriesName = isMoney ? 'Receita' : 'Entregas';

    const chartData = useMemo(
        () =>
            (entries || []).map((entry) => ({
                ...entry,
                shortLabel: entry.label?.length > 24 ? `${entry.label.slice(0, 22)}…` : entry.label,
            })),
        [entries],
    );

    if (!loading && chartData.length === 0) {
        return null;
    }

    return (
        <ChartWrap $minHeight={`${Math.max(220, chartData.length * 36)}px`} aria-busy={loading || undefined}>
            <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 36)}>
                <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme?.colors?.gray?.[200] || '#e5e7eb'} />
                    <XAxis
                        type="number"
                        allowDecimals={isMoney}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => (
                            isMoney
                                ? formatMoneyMetricValue({ value, currency: 'BRL' })
                                : Number(value).toLocaleString('pt-BR')
                        )}
                    />
                    <YAxis
                        type="category"
                        dataKey="shortLabel"
                        width={120}
                        tick={{ fontSize: 11, fill: theme?.colors?.gray?.[700] || '#4a5568' }}
                    />
                    <Tooltip
                        formatter={(value) => [
                            isMoney
                                ? formatMoneyMetricValue({ value, currency: 'BRL' })
                                : Number(value).toLocaleString('pt-BR'),
                            seriesName,
                        ]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ''}
                    />
                    <Bar dataKey="value" fill={fill} radius={[0, 4, 4, 0]} isAnimationActive={!loading} />
                </BarChart>
            </ResponsiveContainer>
        </ChartWrap>
    );
}
